import React, { useEffect, useRef, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import ConfirmModal from '../../src/components/ConfirmModal';
import {
    MOTIVOS_ATRASO_EMISSAO,
    MOTIVO_OUTRO,
    validarJustificativaAtraso,
} from '../../src/lib/janelaFaturamento';
import { formatDateString } from '../../src/lib/janelaCobranca';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Chamado com o par validado. Quem recebe segue para a confirmação de emissão. */
    onConfirm: (dados: { motivo: string; justificativa: string }) => void;
    /** Último dia em que a emissão ainda estaria no prazo padrão. */
    limite: Date;
    /** Dias de atraso já calculados por `exigeJustificativaAtraso`. */
    diasDeAtraso: number;
    /** `data_vencimento` da fatura da Equatorial, só para exibição. */
    dataVencimento?: string | null;
    motivoInicial?: string;
    justificativaInicial?: string;
}

const selectCls =
    'w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ' +
    'text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 ' +
    // Sem isto a lista aberta do <select> herda o tema claro do SO no Chrome/Windows
    // e fica cinza-sobre-branco no dark mode — nenhum select do Financeiro tinha.
    'dark:[color-scheme:dark]';

/**
 * Motivo + justificativa de um boleto emitido fora do prazo padrão.
 *
 * Quem decide se este modal aparece é `exigeJustificativaAtraso`
 * (src/lib/janelaFaturamento.ts) — aqui só se coleta e valida o par. O modal não
 * fecha por clique no fundo nem por ESC, seguindo o que os gates de confirmação do
 * Financeiro já fazem: sair daqui é uma escolha explícita, e cancelar não emite.
 */
const JustificativaAtrasoModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onConfirm,
    limite,
    diasDeAtraso,
    dataVencimento,
    motivoInicial = '',
    justificativaInicial = '',
}) => {
    const [motivo, setMotivo] = useState(motivoInicial);
    const [justificativa, setJustificativa] = useState(justificativaInicial);
    const selectRef = useRef<HTMLSelectElement>(null);

    // Reabrir o modal (ex.: o usuário voltou da conferência para trocar o motivo)
    // tem que reencontrar o que já havia sido escolhido, não um formulário limpo.
    useEffect(() => {
        if (!isOpen) return;
        setMotivo(motivoInicial);
        setJustificativa(justificativaInicial);
        // O select é o primeiro campo obrigatório: é nele que o foco deve cair.
        const t = setTimeout(() => selectRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, [isOpen, motivoInicial, justificativaInicial]);

    const justificativaObrigatoria = motivo === MOTIVO_OUTRO;
    const erro = validarJustificativaAtraso(motivo, justificativa);

    const vencimentoLegivel = dataVencimento
        ? formatDateString(new Date(`${String(dataVencimento).slice(0, 10)}T12:00:00`))
        : null;

    return (
        <ConfirmModal
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={() => onConfirm({ motivo, justificativa: justificativa.trim() })}
            title="Emissão fora do prazo padrão"
            // "Crítico" é o rótulo que a Janela de Faturamento já usa para OUTRA
            // coisa na cabeça de quem opera; aqui o texto diz a data, não o adjetivo.
            message={
                `O prazo padrão para emitir este boleto terminou em ${formatDateString(limite)}` +
                `${vencimentoLegivel ? ` (a fatura da Equatorial vence em ${vencimentoLegivel})` : ''}. ` +
                `Registre o motivo do atraso para seguir com a emissão.`
            }
            confirmLabel="Registrar e continuar"
            cancelLabel="Voltar"
            variant="warning"
            confirmDisabled={!!erro}
        >
            <div className="space-y-3">
                <div className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2">
                    <CalendarClock size={15} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                        {diasDeAtraso === 1
                            ? '1 dia após o prazo padrão de faturamento.'
                            : `${diasDeAtraso} dias após o prazo padrão de faturamento.`}
                    </p>
                </div>

                <div>
                    <label
                        htmlFor="motivo-atraso-emissao"
                        className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1"
                    >
                        Motivo <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="motivo-atraso-emissao"
                        ref={selectRef}
                        required
                        aria-required="true"
                        className={selectCls}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    >
                        <option value="">Selecione o motivo…</option>
                        {MOTIVOS_ATRASO_EMISSAO.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label
                        htmlFor="justificativa-atraso-emissao"
                        className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1"
                    >
                        Justificativa{' '}
                        {justificativaObrigatoria
                            ? <span className="text-red-500">*</span>
                            : <span className="font-normal text-gray-400">(opcional)</span>}
                    </label>
                    <textarea
                        id="justificativa-atraso-emissao"
                        rows={3}
                        required={justificativaObrigatoria}
                        aria-required={justificativaObrigatoria}
                        // O leitor de tela precisa ser avisado de que o campo mudou de
                        // opcional para obrigatório — a troca do asterisco é só visual.
                        aria-describedby="justificativa-atraso-emissao-ajuda"
                        placeholder={
                            justificativaObrigatoria
                                ? 'Descreva o que aconteceu (obrigatório para "Outro")'
                                : 'Detalhe o caso, se ajudar a entender'
                        }
                        className={`w-full px-3 py-2.5 rounded-xl border bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-y ${
                            justificativaObrigatoria
                                ? 'border-amber-400 dark:border-amber-600'
                                : 'border-gray-200 dark:border-gray-700'
                        }`}
                        value={justificativa}
                        onChange={(e) => setJustificativa(e.target.value)}
                    />
                    <p
                        id="justificativa-atraso-emissao-ajuda"
                        aria-live="polite"
                        className="mt-1 text-[11px] text-gray-500 dark:text-gray-400"
                    >
                        {justificativaObrigatoria
                            ? 'Obrigatório porque o motivo selecionado é "Outro".'
                            : 'Opcional para este motivo.'}
                    </p>
                </div>
            </div>
        </ConfirmModal>
    );
};

export default JustificativaAtrasoModal;
